/**
 * Recurring document templates — reads + writes through the Hono backend
 * (`apps/api/src/routes/accounting/recurring.ts`, `/api/v1/recurring/orgs/:orgId/templates…`,
 * gated by the `recurring_documents` feature flag).
 *
 * Liveness contract: EVERY cache in this file nests under
 * `['recurringTemplates', organizationId]` —
 *   index        `['recurringTemplates', orgId, 'index', {}]`     (indexQueryOptions)
 *   detail       `['recurringTemplates', orgId, 'detail', id]`
 *   occurrences  `['recurringTemplates', orgId, 'occurrences', id]`
 * — and every mutation's `onSuccess` invalidates that literal two-slot prefix,
 * so one call refreshes the grid, the open expanded row and its History.
 * "Run now" additionally refreshes the caches of the documents it created
 * (`invalidateGeneratedDocumentCaches`), which reuses the create hooks' own
 * exported invalidation lists so the two can never drift.
 *
 * Errors: `onError` TOASTS ONLY. The app-level `MutationCache.onError` already
 * captures every mutation failure to Sentry (docs/OBSERVABILITY.md) — a second
 * `captureException` here would double-report. The one structured case is
 * 409 `busy` (the template is being generated right now — the sweep or
 * another tab holds its `FOR UPDATE NOWAIT` lock), which gets its own toast.
 * Multi-step flows (create → run → navigate) live in the forms and add a
 * `Sentry.addBreadcrumb` per step; the run mutation drops one here too.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  CreateRecurringTemplateInput,
  RecurringDocumentType,
  RecurringOccurrenceWire,
  RecurringOccurrencesResponse,
  RecurringRunResponse,
  RecurringRunResult,
  RecurringTemplateCreateResponse,
  RecurringTemplateDeleteResponse,
  RecurringTemplateIndexRowWire,
  RecurringTemplateResponse,
  RecurringTemplateWire,
  UpdateRecurringTemplateInput,
} from '@releve/shared';
import i18n from '@/i18n';
import { toast } from '@/hooks/useToast';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import { ApiError, authedDelete, authedGet, authedPatch, authedPost } from '@/lib/api';
import { addBreadcrumb } from '@/lib/sentry';
import { invalidateExpenseCreateCaches } from '@/hooks/useExpenses';
import { invalidateAPBillCreateCaches } from '@/hooks/useAccountsPayable';
import { invalidateARInvoiceCreateCaches } from '@/hooks/useAccountsReceivable';
import { invalidateJournalCreateCaches } from '@/hooks/useLedgerTransactions';

export type RecurringTemplateIndexRow = RecurringTemplateIndexRowWire;
export type RecurringTemplate = RecurringTemplateWire;
export type RecurringOccurrence = RecurringOccurrenceWire;

/** Query-key slot 0 — the prefix every mutation here invalidates. */
export const RECURRING_TEMPLATES_QUERY_KEY = 'recurringTemplates';

/** `/api/v1/recurring/orgs/:orgId/templates` — the collection URL. */
export const recurringTemplatesPath = (organizationId: string) =>
  `/api/v1/recurring/orgs/${organizationId}/templates`;

/** One template's URL (`…/templates/:id`). */
export const recurringTemplatePath = (organizationId: string, templateId: string) =>
  `${recurringTemplatesPath(organizationId)}/${templateId}`;

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * The COMPLETE slim template index for an org (the
 * `recurring_document_template_index` view — every column but `payload`,
 * plus `party_name` + `owner_name`), paged server-side past PostgREST's
 * 1,000-row cap. Powers the `/accounting/recurring` grid's client-side
 * search/sort/filter; the payload is fetched lazily via
 * {@link useRecurringTemplateById} when a template is opened for edit.
 */
export const useRecurringTemplateIndex = (organizationId?: string) =>
  useQuery(
    indexQueryOptions<RecurringTemplateIndexRow>({
      entity: RECURRING_TEMPLATES_QUERY_KEY,
      organizationId,
      path: organizationId ? `${recurringTemplatesPath(organizationId)}/index` : '',
      staleTime: 60_000,
    }),
  );

/** One template WITH its payload — the edit form's source. */
export const useRecurringTemplateById = (organizationId?: string, templateId?: string) =>
  useQuery({
    queryKey: [RECURRING_TEMPLATES_QUERY_KEY, organizationId, 'detail', templateId],
    queryFn: async (): Promise<RecurringTemplate | null> => {
      if (!organizationId || !templateId) return null;
      const result = await authedGet<RecurringTemplateResponse>(
        recurringTemplatePath(organizationId, templateId),
      );
      return result.template;
    },
    enabled: !!organizationId && !!templateId,
    // Only mutations change a template, and every one of them invalidates the
    // prefix — so no focus/remount refetch of a payload that cannot have moved.
    staleTime: 2 * 60 * 1000,
  });

/**
 * A template's generation history (newest first, capped at 100 by the
 * route) — lazy: mounted only by the expanded row's History section.
 */
export const useRecurringOccurrences = (organizationId?: string, templateId?: string) =>
  useQuery({
    queryKey: [RECURRING_TEMPLATES_QUERY_KEY, organizationId, 'occurrences', templateId],
    queryFn: async (): Promise<RecurringOccurrence[]> => {
      if (!organizationId || !templateId) return [];
      const result = await authedGet<RecurringOccurrencesResponse>(
        `${recurringTemplatePath(organizationId, templateId)}/occurrences`,
      );
      return result.occurrences;
    },
    enabled: !!organizationId && !!templateId,
    staleTime: 5 * 60 * 1000,
  });

// ---------------------------------------------------------------------------
// Invalidation
// ---------------------------------------------------------------------------

/**
 * The one prefix every template cache lives under. Returns the refetch
 * promise: mutations `await` it in `onSuccess`, so a mutation stays pending —
 * and the host's `pendingTemplateId` guard stays on — until the fresh rows
 * have landed. Clearing the guard on the POST alone re-enabled the expanded
 * row's action bar in its OLD state for ~60 ms before the index refetch
 * flipped it (a visible Pause → Pause → Resume flicker).
 */
export const invalidateRecurringTemplates = (queryClient: QueryClient, organizationId: string) =>
  queryClient.invalidateQueries({ queryKey: [RECURRING_TEMPLATES_QUERY_KEY, organizationId] });

/**
 * After "Run now" created documents of `documentType`: refresh exactly what
 * the corresponding create hook refreshes after its own create — the SAME
 * exported function, so a cache added to a create hook is refreshed here too.
 */
export const invalidateGeneratedDocumentCaches = (
  queryClient: QueryClient,
  organizationId: string,
  documentType: RecurringDocumentType,
) => {
  switch (documentType) {
    case 'expense':
      invalidateExpenseCreateCaches(queryClient, organizationId);
      return;
    case 'ap_bill':
      invalidateAPBillCreateCaches(queryClient, organizationId);
      return;
    case 'ar_invoice':
      invalidateARInvoiceCreateCaches(queryClient, organizationId);
      return;
    case 'journal':
      invalidateJournalCreateCaches(queryClient, organizationId);
      return;
    default: {
      const never: never = documentType;
      throw new Error(`Unknown recurring document type ${String(never)}`);
    }
  }
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const isBusy = (error: unknown): boolean => error instanceof ApiError && error.status === 409;

/** Failure toast: the 409 `busy` case has its own words; everything else names the action. */
const toastMutationError = (error: unknown, failedKey: string) => {
  if (isBusy(error)) {
    toast.error(i18n.t('recurring.toast.busy'));
    return;
  }
  const description = error instanceof Error && error.message ? error.message : undefined;
  toast.error(i18n.t(failedKey), description ? { description } : undefined);
};

export interface CreateRecurringTemplateArgs {
  organizationId: string;
  data: CreateRecurringTemplateInput;
}

/**
 * `POST /templates` → `{ template, due_now }`. The form reads `due_now` off
 * the result: when the start is org-today it chains {@link useRunRecurringTemplate}
 * so the first document appears immediately.
 */
export const useCreateRecurringTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [RECURRING_TEMPLATES_QUERY_KEY, 'create'],
    mutationFn: async ({ organizationId, data }: CreateRecurringTemplateArgs) =>
      authedPost<RecurringTemplateCreateResponse>(recurringTemplatesPath(organizationId), data),
    onSuccess: async (_, { organizationId }) => {
      await invalidateRecurringTemplates(queryClient, organizationId);
      toast.success(i18n.t('recurring.toast.created'));
    },
    onError: (error) => toastMutationError(error, 'recurring.toast.createFailed'),
  });
};

export interface UpdateRecurringTemplateArgs {
  organizationId: string;
  templateId: string;
  /** Full replace — the same body as create; `document_type` must match the row (else 400). */
  data: UpdateRecurringTemplateInput;
}

export const useUpdateRecurringTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [RECURRING_TEMPLATES_QUERY_KEY, 'update'],
    mutationFn: async ({ organizationId, templateId, data }: UpdateRecurringTemplateArgs) =>
      authedPatch<RecurringTemplateResponse>(recurringTemplatePath(organizationId, templateId), data),
    onSuccess: async (_, { organizationId }) => {
      await invalidateRecurringTemplates(queryClient, organizationId);
      toast.success(i18n.t('recurring.toast.updated'));
    },
    onError: (error) => toastMutationError(error, 'recurring.toast.updateFailed'),
  });
};

export interface RecurringTemplateIdArgs {
  organizationId: string;
  templateId: string;
}

/** `active → paused`. */
export const usePauseRecurringTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [RECURRING_TEMPLATES_QUERY_KEY, 'pause'],
    mutationFn: async ({ organizationId, templateId }: RecurringTemplateIdArgs) =>
      authedPost<RecurringTemplateResponse>(`${recurringTemplatePath(organizationId, templateId)}/pause`, {}),
    onSuccess: async (_, { organizationId }) => {
      await invalidateRecurringTemplates(queryClient, organizationId);
      toast.success(i18n.t('recurring.toast.paused'));
    },
    onError: (error) => toastMutationError(error, 'recurring.toast.pauseFailed'),
  });
};

/**
 * `paused | error → active`. From `error` the failed date is retried; from
 * `paused` the schedule skips to the first date on/after today. Either way
 * the template now runs as the caller (`owner_id` re-stamped server-side).
 */
export const useResumeRecurringTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [RECURRING_TEMPLATES_QUERY_KEY, 'resume'],
    mutationFn: async ({ organizationId, templateId }: RecurringTemplateIdArgs) =>
      authedPost<RecurringTemplateResponse>(`${recurringTemplatePath(organizationId, templateId)}/resume`, {}),
    onSuccess: async (_, { organizationId }) => {
      await invalidateRecurringTemplates(queryClient, organizationId);
      toast.success(i18n.t('recurring.toast.resumed'));
    },
    onError: (error) => toastMutationError(error, 'recurring.toast.resumeFailed'),
  });
};

/** Hard delete — generated documents are kept (occurrences keep the name snapshot). */
export const useDeleteRecurringTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [RECURRING_TEMPLATES_QUERY_KEY, 'delete'],
    mutationFn: async ({ organizationId, templateId }: RecurringTemplateIdArgs) =>
      authedDelete<RecurringTemplateDeleteResponse>(recurringTemplatePath(organizationId, templateId)),
    onSuccess: async (_, { organizationId }) => {
      await invalidateRecurringTemplates(queryClient, organizationId);
      toast.success(i18n.t('recurring.toast.deleted'));
    },
    onError: (error) => toastMutationError(error, 'recurring.toast.deleteFailed'),
  });
};

export interface RunRecurringTemplateArgs extends RecurringTemplateIdArgs {
  /** Which document caches to refresh once the run lands. */
  documentType: RecurringDocumentType;
}

/**
 * "Run now" — generate everything DUE for this template (same core as the
 * pg_cron sweep, `triggered_by = 'manual'`; never "generate early"). A 200
 * can still carry `status: 'error'`: the run stopped on an RPC failure, the
 * template is parked in `error` with `last_error`, and the row shows it —
 * so that success path toasts destructive, not green.
 */
export const useRunRecurringTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [RECURRING_TEMPLATES_QUERY_KEY, 'run'],
    mutationFn: async ({ organizationId, templateId }: RunRecurringTemplateArgs): Promise<RecurringRunResult> => {
      addBreadcrumb('recurring: run now', { organizationId, templateId });
      const result = await authedPost<RecurringRunResponse>(
        `${recurringTemplatePath(organizationId, templateId)}/run`,
        {},
      );
      return result.run;
    },
    onSuccess: async (run, { organizationId, documentType }) => {
      await invalidateRecurringTemplates(queryClient, organizationId);
      if (run.generated > 0) invalidateGeneratedDocumentCaches(queryClient, organizationId, documentType);
      if (run.status === 'error') {
        toast.error(i18n.t('recurring.toast.runError'), {
          description: run.last_error ?? undefined,
        });
        return;
      }
      toast.success(i18n.t('recurring.toast.run', { count: run.generated }));
    },
    onError: (error) => toastMutationError(error, 'recurring.toast.runFailed'),
  });
};
